import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@/components/ui';
import { Lock, CheckCircle2 } from 'lucide-react';

const passwordChangeSchema = z.object({
  currentPassword: z
    .string()
    .min(1, 'Senha atual é obrigatória'),
  newPassword: z
    .string()
    .min(6, 'Senha deve ter no mínimo 6 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;

export function ProfileSettingsForm() {
  const navigate = useNavigate();
  const { changePassword, user } = useAuth();
  const [showPasswords, setShowPasswords] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema) as any,
  });

  const onSubmit = async (data: PasswordChangeFormData) => {
    setIsLoading(true);
    try {
      await changePassword(data.currentPassword, data.newPassword);
      toast.success('Senha alterada com sucesso!');
      reset();
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('Password change failed:', error);
      toast.error(error.response?.data?.error || 'Erro ao alterar senha. Verifique sua senha atual.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <Card variant="shadow">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">
            Configurações de Perfil
          </CardTitle>
          <CardDescription>
            Altere sua senha para manter sua conta segura
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Conta: {user.email}</p>
                  <p className="text-blue-700">Nome: {user.name || 'Não definido'}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Senha Atual *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('currentPassword')}
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Digite sua senha atual"
                  className="pl-10"
                  error={errors.currentPassword?.message}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Nova Senha *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('newPassword')}
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Digite sua nova senha"
                  className="pl-10"
                  error={errors.newPassword?.message}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('confirmPassword')}
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Confirme sua nova senha"
                  className="pl-10"
                  error={errors.confirmPassword?.message}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showPasswords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showPasswords" className="text-sm text-gray-700 cursor-pointer">
                Mostrar senhas
              </label>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-gray-700">Requisitos da senha:</p>
              <ul className="text-xs text-gray-600 space-y-0.5">
                <li>• Mínimo de 6 caracteres</li>
                <li>• Recomendado: use letras, números e caracteres especiais</li>
              </ul>
            </div>

            <div className="flex space-x-3 pt-2">
              <Button
                type="submit"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Salvando...' : 'Alterar Senha'}
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
